// 推廣廣播（2026-10-14）——tag 定向＋圖文＋批次中停，四步精靈
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Users, FileText, Eye, Send, Pause, Play, XCircle, RefreshCw } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface TagOption { tag: string; count: number }
interface CampaignRow {
  id: number; name: string; audience_tags: string; total: number; status: string;
  sent: number; failed: number; remaining: number; created_at: number;
}
interface CsTemplateLite { id: number; title: string; body: string; category: string }

const AdminCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'create' | 'list'>('create');
  const [tags, setTags] = useState<TagOption[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [targetType, setTargetType] = useState<'tags' | 'all' | 'customers'>('tags');
  const [phonesText, setPhonesText] = useState('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [samples, setSamples] = useState<{ phone: string; name: string }[]>([]);
  const [csTemplates, setCsTemplates] = useState<CsTemplateLite[]>([]);
  const [message, setMessage] = useState(
    '🎁 {name}，你係香港辣谷嘅老朋友—— 呢個待遇只係得你哋有😉！\n\n以前買辣粉要搵 Alex 幫手？而家有更快嘅方法——自己上網店直接買👉 hkspicyvalley.com/store\n\n🌟 老朋友專享： *單次買滿 HK$300*\n送 刻上你英文名 嘅 X-1493 百搭辣粉紀念裝一支（一支一刻名，全港獨一無二）\n\n落單時喺「備註」寫低瓶上英文名就係你嘅 ✅'
  );
  const [deadline, setDeadline] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  // 節奏（活動級，預設＝防騷擾基準值）
  const [gapMinS, setGapMinS] = useState(30);
  const [gapMaxS, setGapMaxS] = useState(240);
  const [pauseEvery, setPauseEvery] = useState(30);
  const [pauseMinutes, setPauseMinutes] = useState(120);

  const parsePhones = (): { phones: string[]; names: string[] } => {
    const phones: string[] = [];
    const names: string[] = [];
    for (const line of phonesText.split(/\r?\n/)) {
      const s = line.trim();
      if (!s) continue;
      const m = s.split(/[,,]/, 2);
      phones.push(m[0].trim());
      names.push((m[1] || '').trim());
    }
    return { phones, names };
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  const loadTags = async () => {
    try {
      const res = await apiFetch('/api/public/admin/manual-tags');
      const json = await res.json().catch(() => ({}));
      const list = (json.data ?? json) as { tag: string; count?: number }[];
      setTags((Array.isArray(list) ? list : []).map((t) => ({ tag: String(t.tag || t), count: Number(t.count || 0) })));
    } catch { /* ignore */ }
  };

  const loadCampaigns = async () => {
    try {
      const res = await apiFetch('/api/public/admin/campaigns');
      const json = await res.json().catch(() => ({}));
      setCampaigns((json.data ?? json)?.campaigns || []);
    } catch { /* ignore */ }
  };

  const loadTemplates = async () => {
    try {
      const res = await apiFetch('/api/public/admin/cs-templates');
      const json = await res.json().catch(() => ({}));
      setCsTemplates(((json.data ?? json)?.templates || []).filter((t: CsTemplateLite & { is_active?: number }) => t.is_active === undefined || t.is_active));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/admin'); return; }
    loadTags();
    loadCampaigns();
    loadTemplates();
  }, []);

  useEffect(() => {
    if (targetType === 'tags' && !picked.length) { setPreviewCount(null); setSamples([]); return; }
    const t = setTimeout(async () => {
      try {
        const { phones, names } = parsePhones();
        const res = await apiFetch('/api/public/admin/campaigns/preview', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(targetType === 'tags' ? { tags: picked, target_type: 'tags' }
            : targetType === 'all' ? { target_type: 'all' }
            : { target_type: 'customers', phones, names }),
        });
        const json = await res.json().catch(() => ({}));
        const d = json.data ?? json;
        setPreviewCount(d.count ?? 0);
        setSamples(d.samples || []);
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [targetType, picked, phonesText]);

  const toggleTag = (tag: string) => {
    setPicked((p) => (p.includes(tag) ? p.filter((x) => x !== tag) : [...p, tag]));
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', 'general');
    const res = await apiFetch('/api/public/admin/media/upload', { method: 'POST', body: fd });
    const json = await res.json().catch(() => ({}));
    const url = (json.data ?? json)?.r2_url || (json.data ?? json)?.url || '';
    if (!url) throw new Error('圖片上傳失敗');
    return String(url);
  };

  const launch = async () => {
    const { phones, names } = parsePhones();
    if (targetType === 'tags' && !picked.length) { setMsg('請揀 tag'); return; }
    if (targetType === 'customers' && !phones.length) { setMsg('請填指定名單（每行：電話,名字）'); return; }
    if (!message.trim()) { setMsg('請填文案'); return; }
    if (!confirm(`確認向 ${previewCount ?? 0} 位客戶發送？\n節奏：條間 ${gapMinS}–${gapMaxS}s 隨機＋每 ${pauseEvery} 條停 ${pauseMinutes} 分鐘`)) return;
    setBusy(true);
    setMsg(null);
    try {
      let img = imageUrl;
      if (imageFile) img = await uploadImage(imageFile);
      const res = await apiFetch('/api/public/admin/campaigns/launch', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: campaignName || `推廣 ${new Date().toLocaleDateString('zh-HK')}`,
          message, image_url: img || undefined, deadline,
          target_type: targetType,
          ...(targetType === 'tags' ? { tags: picked } : {}),
          ...(targetType === 'customers' ? { phones, names } : {}),
          gap_min_s: gapMinS, gap_max_s: gapMaxS, pause_every: pauseEvery, pause_minutes: pauseMinutes,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'launch failed');
      setMsg(`✅ 已排程 ${(json.data ?? json)?.total} 條——worker 每 15 分鐘處理一批`);
      loadCampaigns();
    } catch (e) {
      setMsg('失敗：' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  const act = async (id: number, action: 'pause' | 'resume' | 'cancel') => {
    if (!confirm(`確認 ${action}？`)) return;
    await apiFetch(`/api/public/admin/campaigns/${id}/${action}`, { method: 'POST' }).catch(() => {});
    loadCampaigns();
  };

  const statusLabel: Record<string, { label: string; cls: string }> = {
    sending: { label: '發送中', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
    paused: { label: '已暫停', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
    completed: { label: '已完成', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
    cancelled: { label: '已取消', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  };

  return (
    <AdminLayout currentPage="campaigns" onLogout={handleLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-800 dark:text-white">
            <Megaphone size={26} className="text-red-600" /> 推廣廣播
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            對客群定向發送 WhatsApp 推廣（tag／全部／指定名單，自動過濾拒收名單）——節奏每個活動可調（預設防騷擾基準）。
          </p>
        </div>

        <div className="flex w-fit gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-700">
          {([['create', '📨 新廣播'], ['list', '📋 廣播紀錄']] as const).map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-lg px-4 py-2 text-sm font-bold transition ${tab === t ? 'bg-white text-red-600 shadow dark:bg-gray-800' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'create' && (
          <div className="space-y-4">
            {/* Step 1 受眾 */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-800 dark:text-white"><Users size={18} className="text-red-600" /> Step 1・揀受眾</h2>
              <div className="mb-3 flex gap-2">
                {([['tags', '按標籤'], ['all', '全部客戶'], ['customers', '指定名單']] as const).map(([t, label]) => (
                  <button
                    key={t}
                    onClick={() => setTargetType(t)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-bold transition ${targetType === t ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {targetType === 'tags' && (
                <div className="flex flex-wrap gap-2">
                  {tags.length === 0 && <p className="text-sm text-gray-400">載入中…</p>}
                  {tags.map((t) => (
                    <button
                      key={t.tag}
                      onClick={() => toggleTag(t.tag)}
                      className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${picked.includes(t.tag)
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'}`}
                    >
                      {t.tag}{t.count ? ` (${t.count})` : ''}
                    </button>
                  ))}
                </div>
              )}
              {targetType === 'customers' && (
                <div>
                  <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">每行一個：`電話,名字`（名字選填，供 {'{name}'} 個人化）。已自動過濾拒收名單。</p>
                  <textarea
                    value={phonesText}
                    onChange={(e) => setPhonesText(e.target.value)}
                    rows={6}
                    placeholder={'85291230001,阿強\n85291230002'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  />
                </div>
              )}
              {targetType === 'all' && (
                <p className="text-xs text-gray-500 dark:text-gray-400">全部客戶＝會員檔＋導入聯絡人聯集（自動去重＋過濾拒收名單）。</p>
              )}
              {previewCount !== null && (
                <p className="mt-3 text-sm font-bold text-green-700 dark:text-green-400">
                  ✓ 命中 {previewCount} 位客戶{samples.length > 0 && `（例：${samples.slice(0, 3).map((s) => `${s.name} ${s.phone}`).join('、')}）`}
                </p>
              )}
            </div>

            {/* Step 2 內容 */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-800 dark:text-white"><FileText size={18} className="text-red-600" /> Step 2・內容（{`{name}`} 會自動個人化）</h2>
              {csTemplates.length > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    const tpl = csTemplates.find((t) => String(t.id) === e.target.value);
                    if (tpl) setMessage(tpl.body);
                  }}
                  className="mb-2 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                >
                  <option value="">⚡ 由 CS 話術庫帶入模板（選填）…</option>
                  {csTemplates.map((t) => <option key={t.id} value={t.id}>{t.title}（{t.category}）</option>)}
                </select>
              )}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={10}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">圖片（選填）</label>
                  <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    className="mt-1 w-full text-sm text-gray-700 dark:text-gray-300" />
                  {imageUrl && <p className="mt-1 text-[10px] text-green-600">✓ 已上傳 {imageUrl.slice(-40)}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Campaign 名（內部識別）</label>
                  <input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="例：舊客回歸 300 送刻名裝"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">截止日（選填，例：31/10/2026）</label>
                  <input value={deadline} onChange={(e) => setDeadline(e.target.value)} placeholder="31/10/2026"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                </div>
              </div>
            </div>

            {/* Step 3 預覽 */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-800 dark:text-white"><Eye size={18} className="text-red-600" /> Step 3・預覽（第一位受眾視角）</h2>
              <div className="mx-auto max-w-sm rounded-[2rem] border-8 border-stone-800 bg-white p-3 text-sm text-gray-800 shadow-xl dark:bg-gray-900 dark:text-gray-100">
                {imageUrl && <img src={imageUrl} alt="campaign" className="mb-3 w-full rounded-lg" />}
                <p className="whitespace-pre-wrap leading-relaxed">{message.replace(/\{name\}/g, samples[0]?.name || '老朋友')}</p>
                <div className="mt-3 rounded-lg bg-gray-100 px-3 py-2 text-center text-sm font-bold text-blue-700 dark:bg-gray-800 dark:text-blue-300">
                  🛒 去購買
                </div>
              </div>
            </div>

            {/* Step 4 發送 */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-gray-800 dark:text-white"><Send size={18} className="text-red-600" /> Step 4・發送（節奏可調）</h2>
              <div className="grid gap-3 sm:grid-cols-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">條間最短（秒）</label>
                  <input type="number" value={gapMinS} onChange={(e) => setGapMinS(Number(e.target.value) || 30)} min={5} max={600}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">條間最長（秒）</label>
                  <input type="number" value={gapMaxS} onChange={(e) => setGapMaxS(Number(e.target.value) || 240)} min={5} max={3600}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">每 N 條中停</label>
                  <input type="number" value={pauseEvery} onChange={(e) => setPauseEvery(Number(e.target.value) || 30)} min={1} max={1000}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">中停時長（分鐘）</label>
                  <input type="number" value={pauseMinutes} onChange={(e) => setPauseMinutes(Number(e.target.value) || 0)} min={0} max={1440}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                </div>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                節奏：條間 {gapMinS}–{gapMaxS} 秒隨機＋每 {pauseEvery} 條自動停 {pauseMinutes} 分鐘（0＝不停）。
                節奏於發送時凍結進本次活動；發送中可暫停／取消；worker 每 15 分鐘處理一批（每批最多 10 條）。
              </p>
              <button
                onClick={launch}
                disabled={busy || !message.trim() || (targetType === 'tags' && !picked.length) || (targetType === 'customers' && !phonesText.trim())}
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-40"
              >
                <Send size={15} /> {busy ? '處理中…' : `確認發送（${previewCount ?? 0} 人）`}
              </button>
              {msg && <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">{msg}</p>}
            </div>
          </div>
        )}

        {tab === 'list' && (
          <div className="space-y-3">
            <button onClick={() => { loadCampaigns(); loadTags(); }} className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200">
              <RefreshCw size={15} /> 重新整理
            </button>
            {campaigns.length === 0 && <p className="text-sm text-gray-400">暫無廣播記錄</p>}
            {campaigns.map((c) => {
              const st = statusLabel[c.status] || { label: c.status, cls: 'bg-gray-100 text-gray-600' };
              return (
                <div key={c.id} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-bold text-gray-800 dark:text-white">#{c.id} {c.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        已發 {c.sent || 0}／{c.total}｜失敗 {c.failed || 0}｜餘 {c.remaining || 0}
                      </p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-gray-100 dark:bg-gray-700">
                    <div className="h-full rounded-full bg-red-600" style={{ width: `${c.total ? Math.round(((c.sent || 0) / c.total) * 100) : 0}%` }} />
                  </div>
                  <div className="mt-2 flex gap-2">
                    {c.status === 'sending' && (
                      <button onClick={() => act(c.id, 'pause')} className="rounded-lg bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300">
                        <Pause size={12} className="mr-1 inline" /> 暫停
                      </button>
                    )}
                    {c.status === 'paused' && (
                      <button onClick={() => act(c.id, 'resume')} className="rounded-lg bg-green-100 px-3 py-1.5 text-xs font-bold text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300">
                        <Play size={12} className="mr-1 inline" /> 續發
                      </button>
                    )}
                    {(c.status === 'sending' || c.status === 'paused') && (
                      <button onClick={() => act(c.id, 'cancel')} className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300">
                        <XCircle size={12} className="mr-1 inline" /> 取消
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCampaigns;
